import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - 140px);
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.lg}`};
  margin-top: 140px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.background} 0%, ${({ theme }) => `${theme.colors.primary}11`} 100%);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: calc(100vh - 90px);
    margin-top: 90px;
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.md}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: calc(100vh - 80px);
    margin-top: 80px;
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.sm}`};
  }
`;

const ContentCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.xl}`};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}22`};
  max-width: 1000px;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.lg}`};
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const Container = styled.div`
  width: 100%;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  font-size: clamp(2rem, 5vw, 2.5rem);
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  font-weight: 700;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.8rem;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-style: italic;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  font-size: clamp(1.3rem, 4vw, 1.8rem);
  margin-top: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 2px solid #e0e0e0;

  &:first-of-type {
    margin-top: 0;
    border-top: none;
    padding-top: 0;
  }
`;

const SubTitle = styled.h3`
  color: #555;
  font-size: clamp(1.1rem, 3vw, 1.3rem);
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Paragraph = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: 1.6;
  color: #333;
  text-align: justify;
`;

const List = styled.ul`
  margin-left: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    line-height: 1.6;
    color: #333;
  }

  ul {
    margin-top: ${({ theme }) => theme.spacing.sm};
  }
`;

const OrderedList = styled.ol`
  margin-left: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    line-height: 1.6;
    color: #333;
  }
`;

const Highlight = styled.div`
  background-color: #fff3cd;
  padding: ${({ theme }) => theme.spacing.md};
  border-left: 4px solid #ffc107;
  margin: ${({ theme }) => theme.spacing.lg} 0;
  border-radius: 4px;
`;

const PermissionItem = styled.div`
  background-color: #f9f9f9;
  padding: ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.sm} 0;
  border-radius: 6px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary};

  strong {
    color: ${({ theme }) => theme.colors.primary};
  }

  p {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }

  ul {
    margin-top: ${({ theme }) => theme.spacing.sm};
  }
`;

const SectionNumber = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Privacy Policy - Radyofone | Data Privacy & Security';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read the Radyofone Privacy Policy. Learn how we collect, use, and protect your data in our radio streaming app for iOS and Android.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://www.radyofone.com/privacy-policy');
  }, []);

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <PageContainer>
      <ContentCard
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <Container>
          <Title>Privacy Policy</Title>
          <LastUpdated><strong>Last Updated:</strong> February 2026</LastUpdated>

          <Paragraph>Welcome to RadyoFone! This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application ("App"). Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the App.</Paragraph>

          <Highlight>
            <Paragraph><strong>Important:</strong> By using RadyoFone, you consent to the data practices described in this policy. We are committed to protecting your privacy and being transparent about our data practices.</Paragraph>
          </Highlight>

          <Section>
            <SectionTitle><SectionNumber>1.</SectionNumber> Introduction</SectionTitle>
            <Paragraph>RadyoFone ("we," "our," or "us") is a music streaming application that provides access to Arabic and international radio stations and music content. We respect your privacy and are committed to protecting your personal information.</Paragraph>
            <Paragraph>This Privacy Policy applies to all users of the RadyoFone mobile application available on Android and iOS platforms. It describes:</Paragraph>
            <List>
              <li>What information we collect</li>
              <li>How we use and share your information</li>
              <li>Your rights regarding your information</li>
              <li>How we protect your information</li>
            </List>
            <Paragraph>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</Paragraph>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>2.</SectionNumber> Information We Collect</SectionTitle>

            <SubTitle>2.1 Account Information</SubTitle>
            <Paragraph>When you create an account with RadyoFone, we collect:</Paragraph>
            <List>
              <li><strong>Email address</strong> - Required for account creation and authentication</li>
              <li><strong>Username</strong> - Your chosen display name</li>
              <li><strong>Profile picture</strong> - Optional image you upload for your profile</li>
              <li><strong>Password</strong> - Encrypted and stored securely (we cannot see your actual password)</li>
            </List>

            <SubTitle>2.2 Usage Data</SubTitle>
            <Paragraph>We automatically collect information about how you interact with the App, including:</Paragraph>
            <List>
              <li>Stations you listen to and favorite</li>
              <li>Audio playback preferences and settings</li>
              <li>Features you use within the App</li>
              <li>Time spent listening to different stations</li>
              <li>App performance metrics and error logs</li>
            </List>

            <SubTitle>2.3 Device Information</SubTitle>
            <Paragraph>We collect certain information about your device to provide and improve our services:</Paragraph>
            <List>
              <li>Device type (Android or iOS)</li>
              <li>Operating system version</li>
              <li>Device identifiers</li>
              <li>App version</li>
              <li>Device tokens for push notifications</li>
            </List>

            <SubTitle>2.4 Location Data</SubTitle>
            <Paragraph>If you grant location permissions, we may collect:</Paragraph>
            <List>
              <li>Approximate or precise location data (depending on your permission settings)</li>
              <li>This information is used to provide location-based features and content recommendations</li>
              <li>You can disable location services at any time through your device settings</li>
            </List>

            <SubTitle>2.5 Contact Information</SubTitle>
            <Paragraph>If you grant contacts permission, we may access:</Paragraph>
            <List>
              <li>Contact names and information stored on your device</li>
              <li>This is used for sharing features within the App</li>
              <li>We do not store your contacts on our servers</li>
              <li>You can revoke this permission at any time through your device settings</li>
            </List>

            <SubTitle>2.6 Analytics and Crash Reports</SubTitle>
            <Paragraph>We use analytics tools to collect:</Paragraph>
            <List>
              <li>App usage statistics</li>
              <li>Performance metrics</li>
              <li>Crash reports and error logs</li>
              <li>This data helps us improve app stability and user experience</li>
            </List>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>3.</SectionNumber> How We Use Your Information</SectionTitle>

            <SubTitle>3.1 App Functionality</SubTitle>
            <Paragraph>We use your information to:</Paragraph>
            <List>
              <li>Create and manage your account</li>
              <li>Authenticate your identity when you log in</li>
              <li>Save your preferences and favorites</li>
              <li>Provide personalized content recommendations</li>
              <li>Enable features like profile pictures and sharing</li>
            </List>

            <SubTitle>3.2 Service Improvement</SubTitle>
            <Paragraph>We analyze usage data to:</Paragraph>
            <List>
              <li>Improve app performance and stability</li>
              <li>Fix bugs and technical issues</li>
              <li>Understand user preferences and behavior</li>
              <li>Develop new features and enhancements</li>
            </List>

            <SubTitle>3.3 Personalization</SubTitle>
            <Paragraph>We use your data to:</Paragraph>
            <List>
              <li>Customize your listening experience</li>
              <li>Recommend stations and content based on your preferences</li>
              <li>Remember your settings and preferences</li>
            </List>

            <SubTitle>3.4 Analytics</SubTitle>
            <Paragraph>We use analytics services to:</Paragraph>
            <List>
              <li>Track app usage and engagement</li>
              <li>Measure the effectiveness of features</li>
              <li>Understand how users interact with the App</li>
            </List>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>4.</SectionNumber> Permissions Explained</SectionTitle>
            <Paragraph>RadyoFone requests certain permissions to provide full functionality. Here's why we need each permission:</Paragraph>

            <PermissionItem>
              <strong>Camera</strong>
              <Paragraph>We request camera access to allow you to take photos for your profile picture directly within the App. This permission is optional - you can choose to use an existing photo from your gallery instead.</Paragraph>
            </PermissionItem>

            <PermissionItem>
              <strong>Microphone</strong>
              <Paragraph>We request microphone access for voice features and audio recording capabilities within the App. This enables voice-based interactions and audio recording features.</Paragraph>
            </PermissionItem>

            <PermissionItem>
              <strong>Location (Fine and Coarse)</strong>
              <Paragraph>We request location access to provide location-based features, such as recommending nearby stations or location-specific content. This permission is optional and can be disabled in your device settings at any time.</Paragraph>
            </PermissionItem>

            <PermissionItem>
              <strong>Contacts (Read and Write)</strong>
              <Paragraph>We request contacts access to enable sharing features, allowing you to share stations or content with your contacts. We do not store your contacts on our servers. This permission is optional and can be revoked at any time.</Paragraph>
            </PermissionItem>

            <PermissionItem>
              <strong>Notifications</strong>
              <Paragraph>We request notification permission to send you push notifications about new stations, app updates, and important announcements. You can manage notification preferences in the App settings or your device settings.</Paragraph>
            </PermissionItem>

            <PermissionItem>
              <strong>Audio (Record Audio, Modify Audio Settings)</strong>
              <Paragraph>We request audio permissions to enable audio recording features and to control audio playback settings. This ensures proper audio functionality throughout the App.</Paragraph>
            </PermissionItem>

            <PermissionItem>
              <strong>System Permissions</strong>
              <Paragraph>We request various system permissions for essential app functionality:</Paragraph>
              <List>
                <li><strong>Wake Lock</strong> - Keeps the screen on while listening to music</li>
                <li><strong>Vibrate</strong> - Provides haptic feedback for notifications and interactions</li>
                <li><strong>Receive Boot Completed</strong> - Allows the App to restart background services after device reboot</li>
                <li><strong>System Alert Window</strong> - Enables floating player controls and notifications</li>
                <li><strong>Write Settings</strong> - Allows the App to adjust system settings for optimal audio playback</li>
              </List>
            </PermissionItem>

            <PermissionItem>
              <strong>Internet</strong>
              <Paragraph>Internet connectivity is required to stream music and access content. We use this to fetch radio streams, download content, and sync your data.</Paragraph>
            </PermissionItem>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>5.</SectionNumber> Third-Party Services</SectionTitle>
            <Paragraph>RadyoFone uses third-party services that may collect information used to identify you. We work with the following service providers:</Paragraph>

            <SubTitle>5.1 Firebase (Google)</SubTitle>
            <Paragraph>We use Google Firebase services for various app functions:</Paragraph>
            <List>
              <li><strong>Firebase Authentication</strong> - Secure user authentication and account management</li>
              <li><strong>Cloud Firestore</strong> - Database storage for user data, preferences, and favorites</li>
              <li><strong>Cloud Storage</strong> - Storage for user-uploaded content like profile pictures</li>
              <li><strong>Firebase Analytics</strong> - App usage analytics and performance monitoring</li>
              <li><strong>Firebase Crashlytics</strong> - Crash reporting and error tracking</li>
              <li><strong>Firebase Performance Monitoring</strong> - App performance metrics</li>
              <li><strong>Firebase Cloud Messaging</strong> - Push notification delivery</li>
            </List>
            <Paragraph>Firebase may collect device information, usage data, and crash reports. For more information about how Google uses data, please visit: <StyledLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</StyledLink></Paragraph>
            <Paragraph>Firebase Privacy Policy: <StyledLink href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">https://firebase.google.com/support/privacy</StyledLink></Paragraph>

            <SubTitle>5.2 Google Mobile Ads (AdMob)</SubTitle>
            <Paragraph>RadyoFone displays advertisements through Google Mobile Ads (AdMob). AdMob may collect:</Paragraph>
            <List>
              <li>Device identifiers</li>
              <li>Advertising IDs</li>
              <li>Location data (if available)</li>
              <li>App usage information</li>
            </List>
            <Paragraph>These ads help us provide the App free of charge. AdMob uses this data to show you relevant advertisements.</Paragraph>
            <Paragraph>For more information about AdMob's data practices, please visit: <StyledLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</StyledLink></Paragraph>
            <Paragraph>AdMob Privacy Policy: <StyledLink href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noopener noreferrer">https://support.google.com/admob/answer/6128543</StyledLink></Paragraph>

            <SubTitle>5.3 Expo</SubTitle>
            <Paragraph>RadyoFone is built using the Expo framework. Expo provides development tools and services but does not collect personal information from end users of the App.</Paragraph>
            <Paragraph>Expo Privacy Policy: <StyledLink href="https://expo.dev/privacy" target="_blank" rel="noopener noreferrer">https://expo.dev/privacy</StyledLink></Paragraph>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>6.</SectionNumber> Data Storage and Security</SectionTitle>

            <SubTitle>6.1 Where Your Data is Stored</SubTitle>
            <Paragraph>Your data is stored securely using Google Firebase services:</Paragraph>
            <List>
              <li>User account information and preferences are stored in Firebase Cloud Firestore</li>
              <li>Profile pictures and uploaded media are stored in Firebase Cloud Storage</li>
              <li>Data is stored on secure servers managed by Google</li>
              <li>Data may be stored in multiple geographic locations for redundancy and performance</li>
            </List>

            <SubTitle>6.2 Security Measures</SubTitle>
            <Paragraph>We implement various security measures to protect your information:</Paragraph>
            <List>
              <li><strong>Encryption</strong> - Data transmitted between the App and our servers is encrypted using industry-standard SSL/TLS protocols</li>
              <li><strong>Authentication</strong> - Secure password hashing and authentication through Firebase Auth</li>
              <li><strong>Access Controls</strong> - Strict access controls ensure only authorized personnel can access user data</li>
              <li><strong>Regular Security Updates</strong> - We regularly update our systems and dependencies to address security vulnerabilities</li>
              <li><strong>Firebase Security Rules</strong> - Database access is controlled by security rules that prevent unauthorized access</li>
            </List>

            <SubTitle>6.3 Data Encryption</SubTitle>
            <Paragraph>We use encryption to protect your data:</Paragraph>
            <List>
              <li>Data in transit is encrypted using TLS/SSL</li>
              <li>Sensitive information like passwords is hashed using secure algorithms</li>
              <li>Firebase services use encryption at rest for stored data</li>
            </List>

            <Paragraph><strong>Note:</strong> While we implement strong security measures, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security, but we strive to use commercially acceptable means to protect your information.</Paragraph>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>7.</SectionNumber> Data Retention</SectionTitle>

            <SubTitle>7.1 How Long We Keep Your Data</SubTitle>
            <Paragraph>We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy:</Paragraph>
            <List>
              <li><strong>Account Data</strong> - Retained for as long as your account is active</li>
              <li><strong>Usage Data</strong> - Retained for up to 2 years for analytics and service improvement</li>
              <li><strong>Crash Reports</strong> - Retained for up to 90 days to help us fix issues</li>
              <li><strong>Analytics Data</strong> - Aggregated and anonymized data may be retained longer for statistical purposes</li>
            </List>

            <SubTitle>7.2 Account Deletion</SubTitle>
            <Paragraph>You can delete your account at any time by:</Paragraph>
            <List>
              <li>Contacting us at <StyledLink href="mailto:support@radyofone.com">support@radyofone.com</StyledLink> with a deletion request</li>
              <li>Including your account email address in the request</li>
            </List>
            <Paragraph>Upon account deletion:</Paragraph>
            <List>
              <li>Your account information will be deleted from our active databases</li>
              <li>Your profile picture and uploaded content will be removed</li>
              <li>Some information may remain in backup systems for up to 90 days</li>
              <li>Aggregated and anonymized data that cannot identify you may be retained</li>
            </List>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>8.</SectionNumber> Your Rights</SectionTitle>
            <Paragraph>You have certain rights regarding your personal information. Depending on your location, these rights may include:</Paragraph>

            <SubTitle>8.1 Access to Your Data</SubTitle>
            <Paragraph>You have the right to:</Paragraph>
            <List>
              <li>Request a copy of the personal information we hold about you</li>
              <li>Know what data we collect and how we use it</li>
              <li>Request this information by contacting us at <StyledLink href="mailto:support@radyofone.com">support@radyofone.com</StyledLink></li>
            </List>

            <SubTitle>8.2 Data Deletion</SubTitle>
            <Paragraph>You have the right to:</Paragraph>
            <List>
              <li>Request deletion of your personal information</li>
              <li>Delete your account and all associated data</li>
              <li>Request deletion by emailing us at <StyledLink href="mailto:support@radyofone.com">support@radyofone.com</StyledLink></li>
            </List>

            <SubTitle>8.3 Opt-Out Options</SubTitle>
            <Paragraph>You can control certain data collection and usage:</Paragraph>
            <List>
              <li><strong>Location Services</strong> - Disable location permissions in your device settings</li>
              <li><strong>Contacts</strong> - Revoke contacts permission in your device settings</li>
              <li><strong>Notifications</strong> - Manage notification preferences in App settings or device settings</li>
              <li><strong>Analytics</strong> - Some analytics data collection is essential for app functionality and cannot be fully disabled</li>
            </List>

            <SubTitle>8.4 Account Deletion Instructions</SubTitle>
            <Paragraph>To delete your RadyoFone account:</Paragraph>
            <OrderedList>
              <li>Send an email to <StyledLink href="mailto:support@radyofone.com">support@radyofone.com</StyledLink></li>
              <li>Include "Account Deletion Request" in the subject line</li>
              <li>Provide your account email address</li>
              <li>We will process your request within 30 days</li>
              <li>You will receive confirmation once your account has been deleted</li>
            </OrderedList>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>9.</SectionNumber> Children's Privacy</SectionTitle>
            <Paragraph>RadyoFone is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13.</Paragraph>

            <SubTitle>9.1 COPPA Compliance</SubTitle>
            <Paragraph>In compliance with the Children's Online Privacy Protection Act (COPPA):</Paragraph>
            <List>
              <li>We do not knowingly collect personal information from children under 13</li>
              <li>If we discover that we have collected information from a child under 13, we will delete it immediately</li>
              <li>If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately</li>
            </List>

            <SubTitle>9.2 Age Restrictions</SubTitle>
            <Paragraph>By using RadyoFone, you represent that you are at least 13 years old. If you are under 18, you should have your parent's or guardian's permission to use the App.</Paragraph>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>10.</SectionNumber> International Users</SectionTitle>

            <SubTitle>10.1 GDPR Considerations (European Union Users)</SubTitle>
            <Paragraph>If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):</Paragraph>
            <List>
              <li><strong>Right to Access</strong> - You can request a copy of your personal data</li>
              <li><strong>Right to Rectification</strong> - You can request correction of inaccurate data</li>
              <li><strong>Right to Erasure</strong> - You can request deletion of your data</li>
              <li><strong>Right to Restrict Processing</strong> - You can request limitation of data processing</li>
              <li><strong>Right to Data Portability</strong> - You can request your data in a portable format</li>
              <li><strong>Right to Object</strong> - You can object to certain types of data processing</li>
            </List>
            <Paragraph>To exercise these rights, contact us at <StyledLink href="mailto:support@radyofone.com">support@radyofone.com</StyledLink></Paragraph>

            <SubTitle>10.2 Data Transfer Information</SubTitle>
            <Paragraph>Your information may be transferred to and processed in countries other than your country of residence:</Paragraph>
            <List>
              <li>Data is stored on Google Firebase servers, which may be located in various countries</li>
              <li>We use appropriate safeguards to protect your data during transfer</li>
              <li>By using the App, you consent to the transfer of your information to these locations</li>
            </List>

            <SubTitle>10.3 CCPA Considerations (California Users)</SubTitle>
            <Paragraph>If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA):</Paragraph>
            <List>
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information (we do not sell your personal information)</li>
              <li>Right to non-discrimination for exercising your privacy rights</li>
            </List>
            <Paragraph>To exercise these rights, contact us at <StyledLink href="mailto:support@radyofone.com">support@radyofone.com</StyledLink></Paragraph>
          </Section>

          <Section>
            <SectionTitle><SectionNumber>11.</SectionNumber> Changes to Privacy Policy</SectionTitle>
            <Paragraph>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.</Paragraph>

            <SubTitle>11.1 Update Notification Process</SubTitle>
            <Paragraph>When we make changes to this Privacy Policy:</Paragraph>
            <List>
              <li>We will update the "Last Updated" date at the top of this page</li>
              <li>For significant changes, we may notify you through:
                <List>
                  <li>In-app notifications</li>
                  <li>Push notifications</li>
                  <li>Email (if you have provided your email address)</li>
                </List>
              </li>
              <li>We encourage you to review this Privacy Policy periodically</li>
            </List>

            <SubTitle>11.2 Your Continued Use</SubTitle>
            <Paragraph>Your continued use of RadyoFone after any changes to this Privacy Policy constitutes your acceptance of the updated policy. If you do not agree with the changes, you should stop using the App and delete your account.</Paragraph>
          </Section>
        </Container>
      </ContentCard>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;

